/* ============================================================
   Miorenée Wishlist
   ------------------------------------------------------------
   Theme-native wishlist. No app required.

   Storage: localStorage via a swappable adapter. To sync with
   customer metafields later, build a tiny app-proxy endpoint
   and register a custom adapter *before* this script's
   DOMContentLoaded work runs (or call MioWishlist.setAdapter()
   any time — state is re-read from the new adapter):

     MioWishlist.setAdapter({
       async load() { ... return [{ h, id, t }] },
       async save(items) { ... }
     });

   window.MioConfig (set in theme.liquid) provides:
     { customerId, wishlistUrl, moneyFormat, baseCurrency, routes }

   Events (on document):
     mio:wishlist:change  detail: { items, count }
   ============================================================ */
(function () {
  'use strict';
  if (window.MioWishlist) return;

  var CFG = window.MioConfig || {};
  var KEY = 'mio_wishlist_v1';

  /* ---------- default localStorage adapter ---------- */
  var localAdapter = {
    load: function () {
      try {
        var raw = localStorage.getItem(KEY);
        if (!raw) return Promise.resolve([]);
        var data = JSON.parse(raw);
        return Promise.resolve(Array.isArray(data.items) ? data.items : []);
      } catch (e) {
        return Promise.resolve([]);
      }
    },
    save: function (items) {
      try {
        localStorage.setItem(KEY, JSON.stringify({ items: items, updated: Date.now() }));
      } catch (e) { /* storage full / private mode — fail silently */ }
      return Promise.resolve();
    }
  };

  /* ---------- store ---------- */
  var state = { items: [], ready: false };
  var adapter = localAdapter;

  function emit() {
    document.dispatchEvent(new CustomEvent('mio:wishlist:change', {
      detail: { items: state.items.slice(), count: state.items.length }
    }));
  }

  function persist() {
    adapter.save(state.items.slice());
    emit();
  }

  var MioWishlist = {
    get items() { return state.items.slice(); },
    get count() { return state.items.length; },

    has: function (handle) {
      return state.items.some(function (i) { return i.h === handle; });
    },

    add: function (handle, productId) {
      if (!handle || this.has(handle)) return;
      state.items.unshift({ h: handle, id: productId || null, t: Date.now() });
      persist();
    },

    remove: function (handle) {
      var before = state.items.length;
      state.items = state.items.filter(function (i) { return i.h !== handle; });
      if (state.items.length !== before) persist();
    },

    toggle: function (handle, productId) {
      if (this.has(handle)) { this.remove(handle); return false; }
      this.add(handle, productId);
      return true;
    },

    merge: function (handles) {
      var self = this, added = 0;
      (handles || []).forEach(function (h) {
        if (h && !self.has(h)) { state.items.push({ h: h, id: null, t: Date.now() }); added++; }
      });
      if (added) persist();
      return added;
    },

    clear: function () { state.items = []; persist(); },

    shareUrl: function () {
      var base = (CFG.wishlistUrl || '/pages/wishlist');
      var origin = window.location.origin;
      var handles = state.items.map(function (i) { return i.h; }).join(',');
      return origin + base + (handles ? '?shared=' + encodeURIComponent(handles) : '');
    },

    setAdapter: function (a) {
      if (!a || typeof a.load !== 'function' || typeof a.save !== 'function') return;
      adapter = a;
      adapter.load().then(function (items) {
        state.items = Array.isArray(items) ? items : [];
        state.ready = true;
        emit();
      });
    },

    /* Fetch full product data for saved items (used by the grid). */
    fetchProducts: function () {
      var jobs = state.items.map(function (i) {
        return fetch('/products/' + i.h + '.js')
          .then(function (r) { return r.ok ? r.json() : null; })
          .catch(function () { return null; });
      });
      return Promise.all(jobs);
    }
  };

  window.MioWishlist = MioWishlist;

  /* initial load */
  adapter.load().then(function (items) {
    state.items = items;
    state.ready = true;
    emit();
  });

  /* cross-tab sync */
  window.addEventListener('storage', function (e) {
    if (e.key !== KEY) return;
    adapter.load().then(function (items) { state.items = items; emit(); });
  });

  /* ---------- money helper (Shopify format string) ---------- */
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var value = '';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var f = format || CFG.moneyFormat || '₹{{amount}}';

    function delimit(number, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision;
      thousands = thousands || ',';
      decimal = decimal || '.';
      if (isNaN(number) || number == null) return 0;
      number = (number / 100.0).toFixed(precision);
      var parts = number.split('.');
      var d = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var c = parts[1] ? decimal + parts[1] : '';
      return d + c;
    }

    switch ((f.match(placeholderRegex) || [])[1]) {
      case 'amount': value = delimit(cents, 2); break;
      case 'amount_no_decimals': value = delimit(cents, 0); break;
      case 'amount_with_comma_separator': value = delimit(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = delimit(cents, 0, '.', ','); break;
      default: value = delimit(cents, 2);
    }
    return f.replace(placeholderRegex, value);
  }

  /* ---------- <mio-wishlist-toggle> ---------- */
  var HEART_OUTLINE = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M19.84 5.06a5.14 5.14 0 0 0-7.34 0L12 5.57l-.5-.51a5.14 5.14 0 0 0-7.34 0 5.3 5.3 0 0 0 0 7.43L12 20.5l7.84-8.01a5.3 5.3 0 0 0 0-7.43z"/></svg>';
  var HEART_FILL = '<svg viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M19.84 5.06a5.14 5.14 0 0 0-7.34 0L12 5.57l-.5-.51a5.14 5.14 0 0 0-7.34 0 5.3 5.3 0 0 0 0 7.43L12 20.5l7.84-8.01a5.3 5.3 0 0 0 0-7.43z"/></svg>';

  var Toggle = /** @type {any} */ (function () {
    function build(el) {
      var handle = el.dataset.productHandle;
      var pid = el.dataset.productId;
      var labelSave = el.dataset.labelSave || 'Save to wishlist';
      var labelSaved = el.dataset.labelSaved || 'Saved to wishlist';
      var showText = el.dataset.showText === 'true';

      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'mio-wishlist-btn' + (showText ? ' mio-wishlist-btn--text' : '');
      btn.setAttribute('aria-pressed', 'false');
      btn.setAttribute('aria-label', labelSave);

      var icon = document.createElement('span');
      icon.className = 'mio-wishlist-btn__icon';
      btn.appendChild(icon);

      var text;
      if (showText) {
        text = document.createElement('span');
        text.className = 'mio-wishlist-btn__label';
        btn.appendChild(text);
      }

      function render() {
        var saved = MioWishlist.has(handle);
        icon.innerHTML = saved ? HEART_FILL : HEART_OUTLINE;
        btn.setAttribute('aria-pressed', saved ? 'true' : 'false');
        btn.setAttribute('aria-label', saved ? labelSaved : labelSave);
        btn.classList.toggle('is-saved', saved);
        if (text) text.textContent = saved ? labelSaved : labelSave;
      }

      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var nowSaved = MioWishlist.toggle(handle, pid);
        if (nowSaved) {
          btn.classList.remove('mio-pulse');
          void btn.offsetWidth; /* restart animation */
          btn.classList.add('mio-pulse');
        }
      });

      document.addEventListener('mio:wishlist:change', render);
      el.appendChild(btn);
      render();
      /* Handed back so the element can remove it on disconnect. Every toggle
         ever built used to keep this listener alive after its card was gone, so
         after a few filter changes dozens of detached buttons re-rendered on
         every heart tap. */
      return render;
    }

    return function (el) { return build(el); };
  })();

  customElements.define('mio-wishlist-toggle', class extends HTMLElement {
    connectedCallback() {
      /* Built once; the listener is re-attached on every connect, because the
         grid moves these elements rather than recreating them. */
      if (!this._init) {
        this._init = true;
        this._onChange = Toggle(this);
        return;                       /* build() already attached and rendered */
      }
      if (this._onChange) {
        document.addEventListener('mio:wishlist:change', this._onChange);
        this._onChange();
      }
    }
    disconnectedCallback() {
      if (this._onChange) document.removeEventListener('mio:wishlist:change', this._onChange);
    }
  });

  /* ---------- <mio-wishlist-count> ---------- */
  customElements.define('mio-wishlist-count', class extends HTMLElement {
    connectedCallback() {
      var self = this;
      /* Kept on the instance so disconnectedCallback can remove exactly this
         handler. Passing a fresh function to removeEventListener removes
         nothing — the same mistake the theme's own removeTrapFocus makes. */
      if (!this._onChange) {
        this._onChange = function () {
          var n = MioWishlist.count;
          self.textContent = n < 100 ? String(n) : '99+';
          var bubble = self.closest('.mio-wishlist-bubble');
          if (bubble) bubble.classList.toggle('hidden', n === 0);
          var icon = self.closest('.mio-wishlist-header-icon');
          if (icon) icon.classList.toggle('mio-wl-has-items', n > 0);
        };
      }
      document.addEventListener('mio:wishlist:change', this._onChange);
      this._onChange();
    }
    disconnectedCallback() {
      if (this._onChange) document.removeEventListener('mio:wishlist:change', this._onChange);
    }
  });

  /* ---------- <mio-wishlist-grid> (wishlist page) ---------- */
  customElements.define('mio-wishlist-grid', class extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this.strings = {};
      try { this.strings = JSON.parse(this.dataset.strings || '{}'); } catch (e) {}
      var params = new URLSearchParams(window.location.search);
      this.sharedHandles = (params.get('shared') || '')
        .split(',').map(function (s) { return s.trim(); }).filter(Boolean);
      this.isShared = this.sharedHandles.length > 0;
      this._render = this.renderAll.bind(this);
      if (!this.isShared) document.addEventListener('mio:wishlist:change', this._render);
      this.renderAll();
    }

    t(key, fallback) { return this.strings[key] || fallback; }

    renderAll() {
      var self = this;
      var handles = this.isShared
        ? this.sharedHandles
        : MioWishlist.items.map(function (i) { return i.h; });

      /* skip rebuild if nothing changed */
      var sig = handles.join(',');
      if (sig === this._sig) return;

      /* pure removal: animate the leaving card(s) out and slide the
         rest into place — no refetch, no grid flash */
      var grid = this.querySelector('.mio-wishlist-grid');
      if (grid) {
        var oldHandles = (this._sig || '').split(',').filter(Boolean);
        var removed = oldHandles.filter(function (h) { return handles.indexOf(h) < 0; });
        var addedNone = handles.every(function (h) { return oldHandles.indexOf(h) >= 0; });
        if (removed.length && addedNone) {
          this._sig = sig;
          this.animateRemoval(grid, removed, handles.length === 0);
          return;
        }
      }

      this._sig = sig;
      if (!handles.length) { this.renderEmpty(); return; }

      this.innerHTML = '<div class="mio-wishlist-loading"><span class="mio-spinner" aria-hidden="true"></span></div>';

      /* Last write wins. _sig is set before these fetches resolve, so two quick
         removals put two batches in flight and the slower one could land last
         and repaint a grid still containing the item just deleted. Same token
         guard as mio-collection-nav-ajax.js. */
      var token = this._renderToken = (this._renderToken || 0) + 1;

      Promise.all(handles.map(function (h) {
        return fetch('/products/' + h + '.js')
          .then(function (r) { return r.ok ? r.json() : null; })
          .catch(function () { return null; });
      })).then(function (products) {
        if (token !== self._renderToken) return;   /* superseded */
        self.renderGrid(handles, products);
      });
    }

    animateRemoval(grid, removedHandles, nowEmpty) {
      var self = this;
      var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      var leaving = removedHandles.map(function (h) {
        return grid.querySelector('.mio-wishlist-card[data-handle="' + h + '"]');
      }).filter(Boolean);
      var staying = Array.prototype.filter.call(grid.children, function (c) {
        return leaving.indexOf(c) < 0;
      });

      function finish() {
        leaving.forEach(function (c) { c.remove(); });
        if (nowEmpty) { self.renderEmpty(); }
      }

      if (reduce || !leaving.length) { finish(); return; }

      var first = staying.map(function (c) { return c.getBoundingClientRect(); });
      leaving.forEach(function (c) { c.classList.add('mio-wl-leaving'); });

      setTimeout(function () {
        leaving.forEach(function (c) { c.remove(); });
        if (nowEmpty) { self.renderEmpty(); return; }
        /* FLIP the survivors from their old spots to the new layout */
        var last = staying.map(function (c) { return c.getBoundingClientRect(); });
        staying.forEach(function (c, i) {
          var dx = first[i].left - last[i].left;
          var dy = first[i].top - last[i].top;
          if (!dx && !dy) return;
          c.style.transition = 'none';
          c.style.transform = 'translate(' + dx + 'px,' + dy + 'px)';
          requestAnimationFrame(function () {
            c.style.transition = 'transform 0.35s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
            c.style.transform = '';
            setTimeout(function () { c.style.transition = ''; }, 420);
          });
        });
      }, 240);
    }

    renderEmpty() {
      var shopUrl = this.dataset.shopUrl || '/collections/all';
      this.innerHTML =
        '<div class="mio-wishlist-empty">' +
          '<span class="mio-wishlist-empty__icon">' + HEART_OUTLINE + '</span>' +
          '<p class="mio-wishlist-empty__title">' + this.t('empty_title', 'Your wishlist is empty') + '</p>' +
          '<p class="mio-wishlist-empty__text">' + this.t('empty_text', 'Tap the heart on any piece to keep it here.') + '</p>' +
          '<a class="mio-wishlist-empty__cta" href="' + shopUrl + '">' + this.t('empty_cta', 'Explore the collections') + '</a>' +
        '</div>';
    }

    renderGrid(handles, products) {
      var self = this;
      var frag = document.createDocumentFragment();

      /* shared-list banner */
      if (this.isShared) {
        var banner = document.createElement('div');
        banner.className = 'mio-wishlist-shared-banner';
        banner.innerHTML =
          '<p>' + this.t('shared_title', 'A wishlist shared with you') + '</p>' +
          '<button type="button" class="mio-wishlist-merge">' +
            this.t('shared_merge', 'Add these to my wishlist') + '</button>';
        banner.querySelector('.mio-wishlist-merge').addEventListener('click', function () {
          MioWishlist.merge(handles);
          window.location.href = self.dataset.wishlistUrl || window.location.pathname;
        });
        frag.appendChild(banner);
      }

      var grid = document.createElement('div');
      grid.className = 'mio-wishlist-grid';

      products.forEach(function (p, idx) {
        var handle = handles[idx];
        var card = document.createElement('div');
        card.className = 'mio-wishlist-card';
        card.dataset.handle = handle;

        if (!p) {
          /* product deleted or hidden */
          card.innerHTML =
            '<div class="mio-wishlist-card__media mio-wishlist-card__media--empty"></div>' +
            '<div class="mio-wishlist-card__info">' +
              '<p class="mio-wishlist-card__title">' + self.t('unavailable', 'No longer available') + '</p>' +
            '</div>';
          if (!self.isShared) card.appendChild(self.heartToggle(handle, ''));
          grid.appendChild(card);
          return;
        }

        var img = (p.featured_image || (p.images && p.images[0]) || '');
        var imgUrl = img ? img.replace(/(\.[a-zA-Z]{3,4})(\?|$)/, '_600x$1$2') : '';
        var priceHtml = '<span data-mio-money>' + formatMoney(p.price) + '</span>';
        if (p.compare_at_price && p.compare_at_price > p.price) {
          priceHtml += ' <s class="mio-wishlist-card__compare" data-mio-money>' + formatMoney(p.compare_at_price) + '</s>';
        }

        card.innerHTML =
          '<a class="mio-wishlist-card__media" href="' + p.url + '">' +
            (imgUrl ? '<img src="' + imgUrl + '" alt="" loading="lazy">' : '') +
          '</a>' +
          '<div class="mio-wishlist-card__info">' +
            '<a class="mio-wishlist-card__title" href="' + p.url + '">' + p.title + '</a>' +
            '<div class="mio-wishlist-card__price">' + priceHtml +
              (!p.available ? ' <span class="mio-wishlist-card__soldout">' + self.t('sold_out', 'Sold out') + '</span>' : '') +
            '</div>' +
          '</div>';

        if (!self.isShared) card.appendChild(self.heartToggle(handle, p.id));
        grid.appendChild(card);
      });

      frag.appendChild(grid);

      /* footer actions: share */
      if (!this.isShared) {
        var actions = document.createElement('div');
        actions.className = 'mio-wishlist-actions';
        actions.innerHTML =
          '<button type="button" class="mio-wishlist-share">' +
            this.t('share', 'Share wishlist') + '</button>';
        var shareBtn = actions.querySelector('.mio-wishlist-share');
        shareBtn.addEventListener('click', function () {
          var url = MioWishlist.shareUrl();
          if (navigator.share) {
            navigator.share({ title: document.title, url: url }).catch(function () {});
          } else if (navigator.clipboard) {
            navigator.clipboard.writeText(url).then(function () {
              var orig = shareBtn.textContent;
              shareBtn.textContent = self.t('link_copied', 'Link copied');
              setTimeout(function () { shareBtn.textContent = orig; }, 2000);
            });
          }
        });
        frag.appendChild(actions);
      }

      this.innerHTML = '';
      this.appendChild(frag);
    }

    /* Same heart toggle as product cards — filled means saved; tapping
       it unsaves, the change event re-renders the grid. */
    heartToggle(handle, pid) {
      var wrap = document.createElement('div');
      wrap.className = 'mio-wishlist-card-toggle';
      var el = document.createElement('mio-wishlist-toggle');
      el.dataset.productHandle = handle;
      if (pid) el.dataset.productId = String(pid);
      el.dataset.labelSave = this.t('add', 'Save to wishlist');
      el.dataset.labelSaved = this.t('remove', 'Remove from wishlist');
      wrap.appendChild(el);
      return wrap;
    }
  });
})();
